import React from 'react';
import { User, Mail, Phone, Calendar, Hash } from 'lucide-react';
import { StatusBadge } from '@/components/UI/StatusBadge';
import { formatDateTime, formatPhoneNumber } from '@/utils/formatters';
import type { User as UserType } from '@/types';

interface UserSectionProps {
    user: UserType;
}

export const UserSection: React.FC<UserSectionProps> = ({ user }) => {
    const userFields = [
        {
            label: 'User ID',
            value: user.id.toString(),
            icon: Hash,
        },
        {
            label: 'Full Name',
            value: user.full_name,
            icon: User,
        },
        {
            label: 'Email Address',
            value: user.email,
            icon: Mail,
        },
        {
            label: 'Phone Number',
            value: user.phone ? formatPhoneNumber(user.phone) : 'Not provided',
            icon: Phone,
        },
        {
            label: 'Account Created',
            value: formatDateTime(user.created_at),
            icon: Calendar,
        },
        {
            label: 'Last Login',
            value: user.last_login ? formatDateTime(user.last_login) : 'Never',
            icon: Calendar,
        },
    ];

    return (
        <div className="bg-white shadow rounded-lg">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        User Information
                    </h3>
                    <StatusBadge status={user.status} />
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userFields.map((field, index) => {
                        const Icon = field.icon;
                        return (
                            <div key={index} className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <Icon className="h-5 w-5 text-gray-400 mt-1" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-500">
                                        {field.label}
                                    </p>
                                    <p className="text-sm text-gray-900 mt-1 break-words">
                                        {field.value}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Avatar section if available */}
                {user.avatar && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <img
                                    className="h-16 w-16 rounded-full object-cover"
                                    src={user.avatar}
                                    alt={user.full_name}
                                />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Profile Picture</p>
                                <p className="text-sm text-gray-900">
                                    {user.full_name}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserSection;
